// Estilos do Ionic (núcleo + utilitários) e modo escuro por classe (Conta › Aparência; Automático segue o celular).
import '@ionic/core/css/core.css';
import '@ionic/core/css/normalize.css';
import '@ionic/core/css/structure.css';
import '@ionic/core/css/typography.css';
import '@ionic/core/css/padding.css';
import '@ionic/core/css/flex-utils.css';
import '@ionic/core/css/display.css';
import '@ionic/core/css/palettes/dark.class.css';
import './theme/variables.css';
import './theme/app.css';
import './theme/showcase.css';
import './theme/design-system.css';

// Teste de rollback do OTA: um bundle publicado com --broken quebra antes de avisar
// que subiu bem; o plugin então volta sozinho para a versão anterior.
if (import.meta.env.VITE_OTA_BROKEN === 'true') {
  throw new Error('[ota] bundle quebrado de propósito (teste de rollback)');
}

import { setupIonic } from './ionic.js';
import { session } from './services/session.js';
import { me } from './services/me.js';
import { toast } from './lib/ui.js';
import { bootUpdate } from './services/ota.js';
import { boot } from './lib/boot.js';
import { push } from './services/push.js';
import { enableHScroll } from './lib/hscroll.js';
import { go } from './pages/base.js';
import { appLinks } from './services/app-links.js';
import { envBanner } from './lib/env-banner.js';
import { theme } from './services/theme.js';
import { safeArea } from './services/safe-area.js';
import { space } from './services/space.js';

import './pages/login.js';
import './pages/signup.js';
import './pages/confirm.js';
import './pages/forgot.js';
import './pages/complete-profile.js';
import './pages/home.js';
import './pages/garage.js';
import './pages/workshop-vehicles.js';
import './pages/profile.js';
import './pages/offline.js';
import './pages/workshop.js';
import './pages/invite.js';
import './pages/vehicle-add.js';
import './pages/vehicle.js';
import './pages/initial-data.js';
import './pages/km-update.js';
import './pages/vehicle-authorize.js';
import './pages/authorize-scan.js';
import './pages/access-requests.js';
import './pages/workshop-vehicle.js';
import './pages/maintenance-form.js';
import './pages/showcase.js';
import './pages/showcase-workshop.js';
import './pages/workshop-profile.js';
import './pages/review-form.js';
import './pages/showcase-reviews.js';
import './pages/workshop-reviews.js';
import './pages/google-return.js';
import './pages/vehicle-sell.js';
import './pages/vehicle-proof.js';
import './pages/transfer-accept.js';
import { flow } from './lib/flow.js';

theme.init();
safeArea.init();
setupIonic();
envBanner();

// Notificações: tocar abre a tela certa; com o app aberto, vira um aviso no topo.
// App Link (https://ticketcar.com.br/app/... ou /app-dev/... no DEV): abre a tela certa.
//   /app/oficina/<id> → perfil público da oficina (link compartilhado)
appLinks.configure({ open: (route) => go(route, 'root') });

push.configure({
  open: (route) => go(route, 'root'),
  foreground: (n) => toast(`${n.title ?? ''}${n.body ? ` — ${n.body}` : ''}`, 'dark', 5000),
});

/* ------------------------------------------------------------------ guardas */
/*
 * Atenção: quando uma guarda redireciona, o ion-router NÃO roda a guarda da tela
 * de destino. Por isso toda guarda calcula o destino FINAL com resolveStart().
 */

/** Carrega o perfil; em caso de problema devolve o destino adequado. */
async function loadProfile() {
  if (!session.isLoggedIn) return { redirect: '/login' };
  try {
    return await me.load();
  } catch (err) {
    if (err.status === 401) return { redirect: '/login' };
    if (err.code === 'USER_BLOCKED') {
      await session.signOut();
      toast('Sua conta está bloqueada. Fale com o suporte.', 'danger', 5000);
      return { redirect: '/login' };
    }
    return { redirect: '/offline' };
  }
}

/** Para onde o usuário deve ir agora: /login, /complete-profile, início do espaço (/home ou /workshop) ou /offline. */
async function resolveStart() {
  const r = await loadProfile();
  if (r.redirect) return r.redirect;
  if (!r.profileComplete) return '/complete-profile';
  // Convite aberto antes do login/cadastro: volta para ele uma vez.
  if (flow.invite) return `/convite/${encodeURIComponent(flow.invite)}`;
  // Link aberto antes de entrar (ex.: "Informar a km" do e-mail): volta para ele uma vez.
  const back = flow.returnTo;
  if (back) {
    flow.returnTo = '';
    return back;
  }
  return space.home();
}

/** Rota que só decide o destino (ex.: a raiz "/"). */
const toStart = async () => ({ redirect: await resolveStart() });

/** Telas de acesso: quem já está logado vai para o destino certo. */
const guestOnly = async () => (session.isLoggedIn ? { redirect: await resolveStart() } : true);

/** Exige estar logado e com o cadastro completo. */
async function needsProfile() {
  const target = await resolveStart();
  if (space.isHome(target)) return true;
  // vai para o login/cadastro: guarda a tela pedida para voltar depois
  if ((target === '/login' || target === '/complete-profile') && !['/', '/home', '/workshop'].includes(location.pathname)) flow.returnTo = location.pathname;
  // voltando para a tela pedida: é esta mesma, segue
  if (target === location.pathname) return true;
  return { redirect: target };
}

/** Logado, mas ainda sem cadastro completo. */
async function needsIncompleteProfile() {
  const target = await resolveStart();
  return target === '/complete-profile' ? true : { redirect: target };
}

/* ------------------------------------------------------------------- rotas */

const routes = [
  // Raiz: só decide o destino (login, completar cadastro ou início).
  { url: '/', component: 'page-home', beforeEnter: toStart },
  { url: '/login', component: 'page-login', beforeEnter: guestOnly },
  { url: '/signup', component: 'page-signup', beforeEnter: guestOnly },
  { url: '/confirm', component: 'page-confirm', beforeEnter: guestOnly },
  { url: '/forgot', component: 'page-forgot', beforeEnter: guestOnly },
  // Volta do login com Google (a própria tela troca o código e decide o destino)
  { url: '/entrar', component: 'page-google-return' },
  { url: '/complete-profile', component: 'page-complete-profile', beforeEnter: needsIncompleteProfile },
  { url: '/home', component: 'page-home', beforeEnter: needsProfile },
  { url: '/profile', component: 'page-profile', beforeEnter: needsProfile },
  { url: '/garagem', component: 'page-garage', beforeEnter: needsProfile },
  // espaço Empresa: Painel · Veículos · Empresa (dados, linhas, selo, perfil público e equipe)
  { url: '/workshop', component: 'page-workshop', beforeEnter: needsProfile },
  { url: '/oficina/veiculos', component: 'page-workshop-vehicles', beforeEnter: needsProfile },
  { url: '/oficina/empresa', component: 'page-workshop-company', beforeEnter: needsProfile },
  { url: '/empresa/cadastrar', component: 'page-workshop-join', beforeEnter: needsProfile },
  { url: '/oficinas/:workshopId/avaliacoes', component: 'page-showcase-reviews', beforeEnter: needsProfile },
  { url: '/oficinas/:workshopId', component: 'page-showcase-workshop', beforeEnter: needsProfile },
  { url: '/avaliar/:maintenanceId', component: 'page-review-form', beforeEnter: needsProfile },
  { url: '/oficina/avaliacoes', component: 'page-workshop-reviews', beforeEnter: needsProfile },
  { url: '/oficinas', component: 'page-showcase', beforeEnter: needsProfile },
  { url: '/oficina/perfil', component: 'page-workshop-profile', beforeEnter: needsProfile },
  { url: '/vehicles/new', component: 'page-vehicle-add', beforeEnter: needsProfile },
  { url: '/vehicles/:vehicleId/autorizar', component: 'page-vehicle-authorize', beforeEnter: needsProfile },
  { url: '/vehicles/:vehicleId/lembretes', component: 'page-initial-data', beforeEnter: needsProfile },
  { url: '/vehicles/:vehicleId/km', component: 'page-km-update', beforeEnter: needsProfile },
  // venda pelo app: vendedor gera o link/QR; dono provisório envia o CRLV-e no nome dele
  { url: '/vehicles/:vehicleId/vender', component: 'page-vehicle-sell', beforeEnter: needsProfile },
  { url: '/vehicles/:vehicleId/comprovar', component: 'page-vehicle-proof', beforeEnter: needsProfile },
  { url: '/vehicles/:vehicleId', component: 'page-vehicle', beforeEnter: needsProfile },
  { url: '/autorizacao', component: 'page-authorize-scan', beforeEnter: needsProfile },
  // dono: pedidos de acesso que as empresas fizeram pela placa (aceitar com prazo ou recusar)
  { url: '/pedidos-acesso', component: 'page-access-requests', beforeEnter: needsProfile },
  { url: '/oficina/veiculos/:vehicleId/manutencao/:maintenanceId', component: 'page-maintenance-form', beforeEnter: needsProfile },
  { url: '/oficina/veiculos/:vehicleId/manutencao', component: 'page-maintenance-form', beforeEnter: needsProfile },
  { url: '/oficina/veiculos/:vehicleId', component: 'page-workshop-vehicle', beforeEnter: needsProfile },
  // Sem guarda: a própria tela guarda o token e manda para login/cadastro quando precisa.
  { url: '/convite/:token', component: 'page-invite' },
  // link/QR de transferência do vendedor: sem login, a guarda leva para entrar e volta para cá
  { url: '/transferir/:token', component: 'page-transfer-accept', beforeEnter: needsProfile },
  { url: '/offline', component: 'page-offline' },
];

/* ------------------------------------------------------------- abertura */
/*
 * 1. A tela de abertura (#boot, no index.html) já está visível.
 * 2. No app nativo, o OTA confere se há versão nova (máx. 3 s). Se houver, mostra o progresso
 *    e o app reabre já atualizado (esta função não continua).
 * 3. Sem atualização: cria as rotas; as guardas levam ao login ou ao início (sessão válida)
 *    e a tela de abertura some quando a primeira tela aparece.
 */
async function start() {
  await bootUpdate();

  const router = document.createElement('ion-router');
  router.useHash = false;
  for (const r of routes) {
    const route = document.createElement('ion-route');
    route.url = r.url;
    route.component = r.component;
    if (r.beforeEnter) route.beforeEnter = r.beforeEnter;
    router.append(route);
  }
  router.addEventListener('ionRouteDidChange', () => boot.hide(), { once: true });
  setTimeout(() => boot.hide(), 6000); // garantia: nunca fica presa na abertura
  document.querySelector('ion-app').prepend(router);

  session.events.addEventListener('expired', () => {
    toast('Sua sessão expirou. Entre novamente.', 'warning', 4000);
    router.push('/login', 'root');
  });
}

enableHScroll();
start();
